import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const INBOX_PAGE_SIZE = 10;

// Transitions that indicate a transaction is in a bidding state (not yet a real order/sale)
export const BID_TRANSITIONS = [
  'transition/inquire',
  'transition/accept-bid',
  'transition/reject-bid',
  'transition/operator-reject-bid',
];

// Check if a transaction is a bid — reads the flag saved in protectedData at bid creation
export const isBidTransaction = tx => {
  return tx?.attributes?.protectedData?.isBidTransaction === true;
};

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Slice ================ //

const inboxPageSlice = createSlice({
  name: 'InboxPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
    myBidRefs: [],
    listingBidRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const transactions = action.payload.data.data;
        state.fetchInProgress = false;
        state.pagination = action.payload.data.meta;

        if (action.payload.bidView) {
          const bidView = action.payload.bidView;
          state.transactionRefs = [];
          if (bidView === 'listing-bids') {
            state.listingBidRefs = entityRefs(transactions);
            state.myBidRefs = [];
          } else {
            state.myBidRefs = entityRefs(transactions);
            state.listingBidRefs = [];
          }
        } else {
          state.transactionRefs = entityRefs(transactions);
          state.myBidRefs = [];
          state.listingBidRefs = [];
        }
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      });
  },
});

export default inboxPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = (
  { params, search },
  { dispatch, rejectWithValue, extra: sdk, getState }
) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter && tab !== 'bids') {
    // Notifications and favorites use non-transaction data sources.
    return Promise.resolve({ data: { data: [], meta: { totalItems: 0, totalPages: 0, page: 1, perPage: INBOX_PAGE_SIZE } } });
  }

  const { page = 1, sort, view } = parse(search);
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const commonFields = {
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.transaction': [
      'processName',
      'lastTransition',
      'lastTransitionedAt',
      'transitions',
      'payinTotal',
      'payoutTotal',
      'lineItems',
      'metadata',
      'protectedData',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: INBOX_PAGE_SIZE,
    ...getValidInboxSort(sort),
  };

  if (tab === 'bids') {
    // Fetch only the currently selected bids view so pagination works properly.
    const state = typeof getState === 'function' ? getState() : null;
    const currentUserHasListings = !!state?.user?.currentUserHasListings;
    const bidView =
      view === 'listing-bids'
        ? 'listing-bids'
        : view === 'my-bids'
          ? 'my-bids'
          : currentUserHasListings
            ? 'listing-bids'
            : 'my-bids';
    const only = bidView === 'listing-bids' ? 'sale' : 'order';

    const bidsParams = {
      ...commonFields,
      only,
      processNames: ['default-purchase'],
      'meta_isBidTransaction': true,
      // lastTransitions: BID_TRANSITIONS.join(','),
    };

    return sdk.transactions
      .query(bidsParams)
      .then(res => {
        dispatch(addMarketplaceEntities(res));
        return {
          data: {
            ...res.data,
            data: res.data.data.map(tx => ({ ...tx, _bidView: bidView })),
          },
          bidView,
        };
      })
      .catch(e => rejectWithValue(storableError(e)));
  }

  const apiQueryParams = {
    ...commonFields,
    only: onlyFilter,
    processNames,
    'meta_isBidTransaction': false,
    // Exclude bid-state transactions from orders/sales tabs
    lastTransitions: undefined,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('InboxPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return dispatch(loadDataThunk({ params, search }));
};
